import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

export interface CustomAgentToolWiring {
  toolId: string;
  position: number;
  config?: unknown;
}

/**
 * Visibility tier (FRONT-649 / PDEV-6450). Only present once the backend's
 * `agent.custom-agents-visibility-v1` flag is on; `undefined` means the legacy
 * org-shared model, and the UI degrades to its pre-feature behaviour.
 * `org` == "workspace-wide"; `restricted` == shared with specific users.
 */
export type CustomAgentVisibility = 'private' | 'restricted' | 'org';

export interface CustomAgentSummary {
  id: string;
  name: string;
  description: string | null;
  prompt: string;
  model: string | null;
  currentRevisionId: string | null;
  createdByZitadelUserId: string;
  visibility?: CustomAgentVisibility;
  createdAt: string;
  updatedAt: string;
}

export interface CustomAgent extends CustomAgentSummary {
  tools: CustomAgentToolWiring[];
}

export interface CustomAgentRevision {
  id: string;
  agentId: string;
  revisionNumber: number;
  snapshot?: unknown;
  note: string | null;
  createdByZitadelUserId: string;
  createdAt: string;
}

export interface CustomAgentInput {
  name: string;
  description?: string | null;
  prompt?: string;
  model?: string | null;
  tools?: Array<{ toolId: string; position?: number; config?: unknown }>;
  note?: string | null;
  /** PDEV-6450 visibility tier. Honored only when the backend flag is on. */
  visibility?: CustomAgentVisibility;
}

const BASE = '/api/workspace/custom-agents';

export const customAgentKeys = {
  all: ['workspace-custom-agents'] as const,
  list: () => [...customAgentKeys.all, 'list'] as const,
  detail: (id: string) => [...customAgentKeys.all, 'detail', id] as const,
  revisions: (id: string) => [...customAgentKeys.all, 'revisions', id] as const,
  disabled: () => [...customAgentKeys.all, 'disabled'] as const,
};

/** `enabled` lets an always-mounted caller (the command palette, FRONT-2436) read
 *  this list only while it is actually searching; the cache key is unchanged. */
export function useCustomAgents(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customAgentKeys.list(),
    enabled: options?.enabled,
    queryFn: () => api.get<CustomAgentSummary[]>(BASE),
  });
}

export function useCustomAgent(agentId: string | null) {
  return useQuery({
    queryKey: agentId ? customAgentKeys.detail(agentId) : customAgentKeys.all,
    queryFn: () => api.get<CustomAgent>(`${BASE}/${encodeURIComponent(agentId!)}`),
    enabled: !!agentId,
  });
}

export function useCustomAgentRevisions(agentId: string | null) {
  return useQuery({
    queryKey: agentId ? customAgentKeys.revisions(agentId) : customAgentKeys.all,
    queryFn: () =>
      api.get<CustomAgentRevision[]>(`${BASE}/${encodeURIComponent(agentId!)}/revisions`),
    enabled: !!agentId,
  });
}

/**
 * FRONT-1662: seed the list cache from a mutation's own response, so the
 * list is right on the very next paint instead of waiting on
 * `invalidateQueries` below. `invalidateQueries` defaults to
 * `refetchType: 'active'`, which only REFETCHES a list that's currently
 * mounted — after a create, the form immediately navigates to the new
 * agent's edit screen, so nothing is observing the list at that moment and
 * it's merely flagged stale. The next time `/agents` mounts,
 * `useCustomAgents()` synchronously renders that stale cached array before
 * the background refetch lands, so the new agent is missing on first paint.
 *
 * Never seeds a phantom list: if the cache is `undefined` (never fetched),
 * it's returned unchanged so the list still loads normally on first visit.
 */
export function upsertAgentInListCache(qc: QueryClient, agent: CustomAgentSummary): void {
  qc.setQueryData<CustomAgentSummary[]>(customAgentKeys.list(), (prev) => {
    if (!prev) return prev;
    const idx = prev.findIndex((a) => a.id === agent.id);
    if (idx === -1) return [...prev, agent];
    const next = prev.slice();
    next[idx] = agent;
    return next;
  });
}

export function removeAgentFromListCache(qc: QueryClient, agentId: string): void {
  qc.setQueryData<CustomAgentSummary[]>(customAgentKeys.list(), (prev) =>
    prev ? prev.filter((a) => a.id !== agentId) : prev,
  );
}

export function useCreateCustomAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomAgentInput) => api.post<CustomAgent>(BASE, body),
    onSuccess: (created) => {
      upsertAgentInListCache(qc, created);
      qc.setQueryData(customAgentKeys.detail(created.id), created);
      qc.invalidateQueries({ queryKey: customAgentKeys.all });
    },
  });
}

export function useUpdateCustomAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<CustomAgentInput> }) =>
      api.patch<CustomAgent>(`${BASE}/${encodeURIComponent(id)}`, body),
    onSuccess: (updated) => {
      upsertAgentInListCache(qc, updated);
      qc.setQueryData(customAgentKeys.detail(updated.id), updated);
      qc.invalidateQueries({ queryKey: customAgentKeys.all });
    },
  });
}

export function useDeleteCustomAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`${BASE}/${encodeURIComponent(id)}`),
    onSuccess: (_data, id) => {
      removeAgentFromListCache(qc, id);
      qc.removeQueries({ queryKey: customAgentKeys.detail(id) });
      qc.invalidateQueries({ queryKey: customAgentKeys.all });
    },
  });
}

export function useRevertCustomAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, revisionId, note }: { id: string; revisionId: string; note?: string }) =>
      api.post<CustomAgent>(`${BASE}/${encodeURIComponent(id)}/revert`, { revisionId, note }),
    onSuccess: (reverted) => {
      upsertAgentInListCache(qc, reverted);
      qc.setQueryData(customAgentKeys.detail(reverted.id), reverted);
      qc.invalidateQueries({ queryKey: customAgentKeys.all });
    },
  });
}

/**
 * The tenant's switched-off custom agents (a denylist). An agent is ON when
 * its id is NOT in this list. Empty = every custom agent is on (default).
 */
export function useDisabledCustomAgentIds(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: customAgentKeys.disabled(),
    enabled: options?.enabled,
    queryFn: async (): Promise<string[]> => {
      const res = await api.get<{ disabledAgents: string[] }>(
        '/api/disabled-custom-agents',
      );
      return res.disabledAgents ?? [];
    },
  });
}

/**
 * Toggle one custom agent. `enabled` true → remove from the denylist;
 * false → add to it. Sends the full new denylist back.
 */
export function useToggleCustomAgentEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      agentId,
      enabled,
      currentDisabled,
    }: {
      agentId: string;
      enabled: boolean;
      currentDisabled: string[];
    }) => {
      const next = enabled
        ? currentDisabled.filter((id) => id !== agentId)
        : Array.from(new Set([...currentDisabled, agentId]));
      return api.put<{ ok: boolean; disabledAgents: string[] }>(
        '/api/disabled-custom-agents',
        { agentIds: next },
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: customAgentKeys.disabled() }),
  });
}

/**
 * Master switch. Pass `[]` to enable all custom agents, or every current
 * custom-agent id to disable all.
 */
export function useSetCustomAgentsEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (disabledIds: string[]) =>
      api.put<{ ok: boolean; disabledAgents: string[] }>(
        '/api/disabled-custom-agents',
        { agentIds: disabledIds },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: customAgentKeys.disabled() }),
  });
}
